import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, message } from 'antd';
import { User, Lock, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

//

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError, isAuthenticated } = useAuthStore();
  const [form] = Form.useForm();
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (error) {
      message.error(error);
      clearError();
    }
  }, [error, clearError]);

  const handleSubmit = async (values: { email: string; password: string }) => {
    try {
      // 개발용 특정 계정으로 바로 로그인 처리
      if (values.email === 'bookrum@bookrum.co.kr' && values.password === 'admin12345') {
        // 개발용 자동 로그인 활성화
        const { enableDevAutoLogin } = await import('../utils/devAuth');
        enableDevAutoLogin();
        message.success('로그인 성공! 개발 모드로 접속합니다.');
        navigate('/dashboard');
        window.location.reload(); // AuthStore 상태 업데이트를 위해
        return;
      }
      
      await login(values.email, values.password);
      message.success('로그인 성공!');
      navigate('/dashboard');
    } catch (error) {
      // Error is handled in the store
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden transition-colors duration-300" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Background overlay */}
      <div className="absolute inset-0 opacity-70" style={{ backgroundColor: 'var(--bg-primary)' }}></div>
      
      <div className="relative z-10 min-h-screen flex">
        {/* Left Side - Brand */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-center px-12">
          <div className="max-w-md">
            <div className="flex items-center justify-center mb-8">
              <img 
                src="/BOOKRUM_LOGO_CMS_ERP.png" 
                alt="BOOKRUM ERP" 
                className="h-32 w-auto"
              />
            </div>
            <h2 className="font-serif text-2xl mb-4 text-center" style={{ color: 'var(--text-primary)' }}>
              부크럼 출판사 ERP
            </h2>
            <p className="text-lg mb-6 text-center" style={{ color: 'var(--text-secondary)' }}>
              어쩌면 당신도 읽고 있는 책,<br />
              부크럼 출판사입니다.
            </p>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <img 
                  src="/BOOKRUM_LOGO_CMS_ERP.png" 
                  alt="BOOKRUM ERP" 
                  className="h-16 w-auto"
                />
              </div>
              <p style={{ color: 'var(--text-secondary)' }}>부크럼 출판사 ERP</p>
            </div>

            {/* Login Card */}
            <div className="backdrop-blur-sm rounded-2xl border p-8 shadow-lg transition-colors duration-300" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border)' }}>
              <div className="mb-6">
                <h2 className="text-2xl font-serif font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                  로그인
                </h2>
                <p style={{ color: 'var(--text-secondary)' }}>
                  계정으로 로그인하세요
                </p>
              </div>

              <Form form={form} layout="vertical" onFinish={handleSubmit}>
                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 mb-6">
                    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                {/* Email Field */}
                <Form.Item
                  label={<span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>이메일</span>}
                  name="email"
                  rules={[
                    { required: true, message: '이메일을 입력해주세요' },
                    { type: 'email', message: '올바른 이메일 형식이 아닙니다' }
                  ]}
                  className="mb-4"
                >
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-4 w-4" style={{ color: 'var(--text-secondary)' }} />
                    </div>
                    <Input
                      className="input pl-9"
                      placeholder="email@bookrum.co.kr"
                      autoComplete="email"
                    />
                  </div>
                </Form.Item>

                {/* Password Field */}
                <Form.Item
                  label={<span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>비밀번호</span>}
                  name="password"
                  rules={[
                    { required: true, message: '비밀번호를 입력해주세요' },
                    { min: 6, message: '비밀번호는 최소 6자 이상이어야 합니다' }
                  ]}
                  className="mb-6"
                >
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4" style={{ color: 'var(--text-secondary)' }} />
                    </div>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      className="input pl-9 pr-10"
                      placeholder="••••••••"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center hover:opacity-80 transition-opacity"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </Form.Item>

                {/* Submit Button */}
                <Form.Item className="mb-0">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    className="btn-primary w-full"
                  >
                    {isLoading ? '로그인 중...' : '로그인'}
                  </Button>
                </Form.Item>
              </Form>
            </div>

            {/* Footer */}
            <div className="mt-8 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
              <p>
                © {new Date().getFullYear()} 부크럼. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
